/**
 * ToolsRoutes — Interactive risk tools, Automation gallery, member
 * dashboards, Carousel Lite, team workspace, saved templates.
 *
 * Lazy-loaded because most visitors land on /, /library or /india first;
 * tool pages are deeper in the funnel.
 */
import { lazy } from "react";
import { Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";

const Tools = lazy(() => import("@/pages/Tools"));
const ToolDetail = lazy(() => import("@/pages/ToolDetail"));

// Static tool pages (explicit slugs)
const DpdpQuiz = lazy(() => import("@/pages/tools/DpdpQuiz"));
const DpdpScorer = lazy(() => import("@/pages/tools/DpdpScorer"));
const ComplianceCost = lazy(() => import("@/pages/tools/ComplianceCost"));
const DeadlineCalendar = lazy(() => import("@/pages/tools/DeadlineCalendar"));
const GrcMaturity = lazy(() => import("@/pages/tools/GrcMaturity"));
const IndiaComparison = lazy(() => import("@/pages/tools/IndiaComparison"));
const SalesTrigger = lazy(() => import("@/pages/tools/SalesTrigger"));
const ModelTiering = lazy(() => import("@/pages/tools/ModelTiering"));
const ModelInventory = lazy(() => import("@/pages/tools/ModelInventory"));
const AiVendorChecklist = lazy(() => import("@/pages/tools/AiVendorChecklist"));
const MrmReadiness = lazy(() => import("@/pages/tools/MrmReadiness"));

// Automation gallery
const AutomationTemplates = lazy(() => import("@/pages/AutomationTemplates"));
const AutomationTemplateDetail = lazy(() => import("@/pages/AutomationTemplateDetail"));
const MyWorkflows = lazy(() => import("@/pages/MyWorkflows"));

// Member dashboards / workspaces
const CarouselLite = lazy(() => import("@/pages/CarouselLite"));
const SavedTemplates = lazy(() => import("@/pages/SavedTemplates"));
const Team = lazy(() => import("@/pages/Team"));
const Finder = lazy(() => import("@/pages/Finder"));
const Results = lazy(() => import("@/pages/Results"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DashboardApi = lazy(() => import("@/pages/DashboardApi"));
const DashboardReseller = lazy(() => import("@/pages/DashboardReseller"));
const DashboardEmployer = lazy(() => import("@/pages/DashboardEmployer"));
const DashboardSpeaker = lazy(() => import("@/pages/DashboardSpeaker"));
const DashboardWebinars = lazy(() => import("@/pages/DashboardWebinars"));

export const toolsRoutes = [
  // Tools gallery
  <Route key="tools" path="/tools" element={<Tools />} />,

  // Explicit static tool slugs (these must match BEFORE /tools/:slug)
  <Route key="tools-dpdp-quiz" path="/tools/dpdp-quiz" element={<DpdpQuiz />} />,
  <Route key="tools-dpdp-scorer" path="/tools/dpdp-scorer" element={<DpdpScorer />} />,
  <Route key="tools-compliance-cost" path="/tools/compliance-cost" element={<ComplianceCost />} />,
  <Route key="tools-deadline" path="/tools/deadline-calendar" element={<DeadlineCalendar />} />,
  <Route key="tools-grc-maturity" path="/tools/grc-maturity" element={<GrcMaturity />} />,
  <Route key="tools-india-comparison" path="/tools/india-comparison" element={<IndiaComparison />} />,
  <Route key="tools-sales-trigger" path="/tools/sales-trigger-calendar" element={<SalesTrigger />} />,
  // RBI Model Risk 2026 (Draft) — Model Tiering worksheet + minimal Inventory receiver
  <Route key="tools-model-tiering" path="/tools/model-tiering" element={<ModelTiering />} />,
  <Route key="tools-model-inventory" path="/tools/model-inventory" element={<ModelInventory />} />,
  <Route key="tools-ai-vendor-checklist" path="/tools/ai-vendor-checklist" element={<AiVendorChecklist />} />,
  <Route key="tools-mrm-readiness" path="/tools/mrm-readiness" element={<MrmReadiness />} />,

  // Dynamic tool detail (protected)
  <Route key="tools-slug" path="/tools/:slug" element={<ProtectedRoute><ToolDetail /></ProtectedRoute>} />,

  // Automations
  <Route key="automations" path="/automations" element={<AutomationTemplates />} />,
  <Route key="automations-slug" path="/automations/:slug" element={<AutomationTemplateDetail />} />,
  <Route key="my-workflows" path="/my-workflows" element={<ProtectedRoute><MyWorkflows /></ProtectedRoute>} />,

  // Carousel / Saved / Team / Finder / Results
  <Route key="carousel-lite" path="/carousel-lite" element={<ProtectedRoute><CarouselLite /></ProtectedRoute>} />,
  <Route key="saved-templates" path="/saved-templates" element={<ProtectedRoute><SavedTemplates /></ProtectedRoute>} />,
  <Route key="team" path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />,
  <Route key="finder" path="/finder" element={<ProtectedRoute><Finder /></ProtectedRoute>} />,
  <Route key="results" path="/results/:id" element={<ProtectedRoute><Results /></ProtectedRoute>} />,

  // Member dashboards
  <Route key="dashboard" path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />,
  <Route key="dashboard-api" path="/dashboard/api" element={<DashboardApi />} />,
  <Route key="dashboard-reseller" path="/dashboard/reseller" element={<DashboardReseller />} />,
  <Route key="dashboard-employer" path="/dashboard/employer" element={<ProtectedRoute><DashboardEmployer /></ProtectedRoute>} />,
  <Route key="dashboard-speaker" path="/dashboard/speaker" element={<ProtectedRoute><DashboardSpeaker /></ProtectedRoute>} />,
  <Route key="dashboard-webinars" path="/dashboard/webinars" element={<ProtectedRoute><DashboardWebinars /></ProtectedRoute>} />,
];
